import { Boundaries } from "@planzone/core";
import { events } from "@planzone/events";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { createStateReducer } from "../../utils/createStateReducer";
import { Selector, State } from "./types";
import { createSelector } from "./selector";
import { Assignable, Removable } from "@planzone/dto";

const initialState: State = {
  request: {
    isPending: false,
    isSuccessful: false,
    isFailure: false,
  },
  tags: [],
};

function isResourceTag(assignable: Assignable | Removable) {
  return assignable === Assignable.RESSOURCE;
}

function handleRequest(state: State): State {
  return { ...state, request: setRequestStateToPending() };
}

function handleSuccess(
  state: State,
  tags: Boundaries["retrieveResourcesTags"]["success"],
): State {
  return { ...state, request: setRequestStateToSuccess(), tags };
}

function handleFailure(state: State): State {
  return { ...state, request: setRequestStateToFailure() };
}

function handleAssignTagRequested(
  state: State,
  { tag, assignable }: Boundaries["assignTag"]["request"],
): State {
  const isDuplicate = !!state.tags.find(({ name }) => name === tag.name);

  return isResourceTag(assignable)
    ? {
        ...state,
        tags: !isDuplicate ? [tag, ...state.tags] : state.tags,
      }
    : state;
}

function handleAssignTagSuccess(
  state: State,
  { tag, assignable }: Boundaries["assignTag"]["success"],
): State {
  const isDuplicate = !!state.tags.find(({ name }) => name === tag.name);

  return isResourceTag(assignable)
    ? {
        ...state,
        tags: !isDuplicate ? [tag, ...state.tags] : state.tags,
      }
    : state;
}

function handleAssignTagFailure(
  state: State,
  { tag, assignable }: Boundaries["assignTag"]["failure"],
): State {
  return isResourceTag(assignable)
    ? {
        ...state,
        tags: state.tags.filter(({ name }) => name !== tag.name),
      }
    : state;
}

function handleRemoveTagRequested(
  state: State,
  { tag, removable }: Boundaries["removeTag"]["request"],
): State {
  return isResourceTag(removable)
    ? {
        ...state,
        tags: state.tags.filter(({ name }) => name !== tag.name),
      }
    : state;
}

function handleRemoveTagSuccess(
  state: State,
  { tag, removable }: Boundaries["removeTag"]["success"],
): State {
  return isResourceTag(removable)
    ? {
        ...state,
        tags: state.tags.filter(({ name }) => name !== tag.name),
      }
    : state;
}

function handleRemoveTagFailure(
  state: State,
  { tag, removable }: Boundaries["removeTag"]["failure"],
): State {
  const isDuplicate = !!state.tags.find(({ name }) => name === tag.name);

  return isResourceTag(removable)
    ? {
        ...state,
        tags: !isDuplicate ? [tag, ...state.tags] : state.tags,
      }
    : state;
}

const reduceState = createStateReducer(initialState, {
  [events.retrieveResourcesTagsRequested]: handleRequest,
  [events.retrieveResourcesTagsSucceeded]: handleSuccess,
  [events.retrieveResourcesTagsFailed]: handleFailure,
  [events.assignTagRequested]: handleAssignTagRequested,
  [events.assignTagSucceeded]: handleAssignTagSuccess,
  [events.assignTagFailed]: handleAssignTagFailure,
  [events.removeTagRequested]: handleRemoveTagRequested,
  [events.removeTagSucceeded]: handleRemoveTagSuccess,
  [events.removeTagFailed]: handleRemoveTagFailure,
});

export type { State, Selector };

export { reduceState, initialState, createSelector };
